import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react';
import { ApiError } from '../api/client.ts';
import { loginUrl } from './auth.ts';

export interface Loaded<T> { data: T | undefined; error: Error | undefined; loading: boolean; reload: () => void }

/** Load-on-mount + reload; a 401 sends the user to sign in (the session expired under them). */
export function useLoad<T>(fn: (signal: AbortSignal) => Promise<T>, deps: DependencyList): Loaded<T> {
  const [data, setData] = useState<T>(); const [error, setError] = useState<Error>(); const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0); const fnRef = useRef(fn); fnRef.current = fn;
  useEffect(() => {
    const ac = new AbortController(); setLoading(true);
    fnRef.current(ac.signal).then((d) => { if (!ac.signal.aborted) { setData(d); setError(undefined); } })
      .catch((e: unknown) => { if (ac.signal.aborted) return; if (e instanceof ApiError && e.unauthenticated) { location.assign(loginUrl()); return; } setError(e instanceof Error ? e : new Error(String(e))); })
      .finally(() => { if (!ac.signal.aborted) setLoading(false); });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload };
}

/** Poll while `active` — the list screens use it so 'Leyendo…' turns into 'Por revisar' without a pull-to-refresh. */
export function usePoll(reload: () => void, ms: number, active: boolean): void {
  useEffect(() => { if (!active) return; const id = setInterval(reload, ms); return () => clearInterval(id); }, [reload, ms, active]);
}

export function useMedia(query: string): boolean {
  const get = () => typeof matchMedia !== 'undefined' && matchMedia(query).matches;
  const [m, set] = useState(get);
  useEffect(() => { const mq = matchMedia(query); const on = () => set(mq.matches); mq.addEventListener('change', on); on(); return () => mq.removeEventListener('change', on); }, [query]);
  return m;
}
export const useDesktop = () => useMedia('(min-width: 1024px)');
